import railsFetch from "lib/railsFetch";
import { I18n } from "lib/I18n";
import { PREVIEW_TYPES } from "./constants";

const BASE_PATH = "/api/v1/assessment-framework";

export function assessmentAttemptHref(attemptId) {
  return `${BASE_PATH}/assessment-attempts/${attemptId}`;
}

export function assessmentResultHref(attemptId) {
  return `${BASE_PATH}/assessment-results/${attemptId}`;
}

// Preview signal. Supported values are the PREVIEW_TYPES: the learner type draws the
// pool_config subset from the published definition; the author type surfaces the full
// authored bank of an unpublished definition. Neither persists. The server deduces audience from
// the definition, so it is never leaked in the request.
const PREVIEW_HEADER = "X-Assessment-Preview";

// The show page lives under a locale-prefixed path (/de/...), but these
// endpoints are /api/v1/... so the browser's Accept-Language (often en-US)
// would otherwise win. Always send the Trailhead page locale; add the preview
// header only when previewing (learner wins if both flags are passed).
function withAssessmentHeaders(request, { preview = false, learnerPreview = false } = {}) {
  const headers = {
    ...request.headers,
    "Accept-Language": I18n.locale,
  };

  if (preview || learnerPreview) {
    headers[PREVIEW_HEADER] = learnerPreview ? PREVIEW_TYPES.LEARNER : PREVIEW_TYPES.AUTHOR;
  }

  return { ...request, headers };
}

// Trail context lets the server report the launch's attempt cap: a standard
// Trail step is uncapped, as at start and submit.
export function getPresentation(assessmentDefinitionId, { preview = false, learnerPreview = false, contentType = null, contentId = null } = {}) {
  const query = contentType && contentId
    ? `?${new URLSearchParams({ content_type: contentType, content_id: contentId })}`
    : "";

  return railsFetch(withAssessmentHeaders({
    url: `${BASE_PATH}/assessment-presentations/${assessmentDefinitionId}${query}`,
    method: "get",
  }, { preview, learnerPreview }));
}

// Follow a hypermedia link.
// The server owns the path. Pass method when the link is not a GET.
// No body: a retry re-grades the answers already stored on the attempt.
export function fetchFromHref(href, { method = "get", preview = false, learnerPreview = false } = {}) {
  return railsFetch(withAssessmentHeaders({
    url: href,
    method,
  }, { preview, learnerPreview }));
}

export function getAssessmentAttempt(attemptId) {
  return railsFetch(withAssessmentHeaders({
    url: assessmentAttemptHref(attemptId),
    method: "get",
  }));
}

export function startAttempt(assessmentDefinitionId, { preview = false, learnerPreview = false, contentType = null, contentId = null } = {}) {
  const data = {
    assessment_definition_id: assessmentDefinitionId,
  };

  if (contentType && contentId) {
    data.content_type = contentType;
    data.content_id = contentId;
  }

  return railsFetch(withAssessmentHeaders({
    url: `${BASE_PATH}/assessment-attempts`,
    method: "post",
    data,
  }, { preview, learnerPreview }));
}

export function submitResults(assessmentDefinitionId, attemptId, responses, { preview = false, learnerPreview = false, contentType = null, contentId = null } = {}) {
  const data = {
    assessment_definition_id: assessmentDefinitionId,
    attempt_id: attemptId,
    responses,
  };

  if (contentType && contentId) {
    data.content_type = contentType;
    data.content_id = contentId;
  }

  return railsFetch(withAssessmentHeaders({
    url: `${BASE_PATH}/assessment-results`,
    method: "post",
    data,
  }, { preview, learnerPreview }));
}
